import { useState, useCallback } from 'react';

/**
 * @hook useClipboard
 * @description Hook para manejar operaciones de clipboard con feedback visual
 */

interface UseClipboardOptions {
  /** Duración del feedback en milisegundos */
  feedbackDuration?: number;
  /** Callback cuando se copia exitosamente */
  onSuccess?: (text: string) => void;
  /** Callback cuando falla la copia */
  onError?: (error: Error) => void;
}

interface UseClipboardReturn {
  /** Función para copiar texto al clipboard */
  copy: (text: string) => Promise<boolean>;
  /** Estado indicando si se copió recientemente */
  copied: boolean;
  /** Error si ocurrió alguno */
  error: string | null;
  /** Estado de carga */
  loading: boolean;
}

export const useClipboard = (options: UseClipboardOptions = {}): UseClipboardReturn => {
  const {
    feedbackDuration = 2000,
    onSuccess,
    onError
  } = options;

  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const copy = useCallback(async (text: string): Promise<boolean> => {
    if (!text) {
      setError('No hay texto para copiar');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Intentar usar la API moderna de clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback para navegadores más antiguos o contextos no seguros
        await fallbackCopyTextToClipboard(text);
      }

      setCopied(true);
      onSuccess?.(text);

      // Resetear el estado después del tiempo especificado
      setTimeout(() => {
        setCopied(false);
      }, feedbackDuration);

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al copiar al clipboard';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
      return false;
    } finally {
      setLoading(false);
    }
  }, [feedbackDuration, onSuccess, onError]);

  return {
    copy,
    copied,
    error,
    loading
  };
};

/**
 * Función de fallback para copiar texto en navegadores que no soportan la API moderna
 */
const fallbackCopyTextToClipboard = async (text: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // Hacer el textarea invisible pero accesible
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    textArea.setAttribute('readonly', '');
    textArea.setAttribute('aria-hidden', 'true');
    
    document.body.appendChild(textArea);
    
    try {
      textArea.focus();
      textArea.select();
      
      // Para dispositivos móviles
      textArea.setSelectionRange(0, 99999);
      
      const successful = document.execCommand('copy');
      
      if (successful) {
        resolve();
      } else {
        reject(new Error('No se pudo copiar usando execCommand'));
      }
    } catch (err) {
      reject(err);
    } finally {
      document.body.removeChild(textArea);
    }
  });
};

/**
 * @hook useClipboardWithToast
 * @description Hook que combina clipboard con notificaciones toast
 */
export const useClipboardWithToast = (options: UseClipboardOptions = {}) => {
  const clipboard = useClipboard({
    ...options,
    onSuccess: (text) => {
      // Aquí se podría integrar con un sistema de toast/notificaciones
      console.log(`✅ Copiado: ${text}`);
      options.onSuccess?.(text);
    },
    onError: (error) => {
      console.error('❌ Error al copiar:', error.message);
      options.onError?.(error);
    }
  });

  return clipboard;
};
